import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { messageAPI } from '../services/api';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const Messages = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(true);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldScrollRef = useRef(true);

  // Fetch conversations and notifications on mount
  useEffect(() => {
    fetchConversations();
    fetchNotifications();

    // Reset unread count when visiting messages page
    // This will be picked up by parent component via event or context
    window.dispatchEvent(new CustomEvent('messagesPageVisited'));
  }, []);

  // Handle navigation from ShareManager
  useEffect(() => {
    const initializeConversation = async () => {
      if (!location.state?.userId) return;

      const targetUserId = location.state.userId;

      // Check if conversation already exists
      const existingConv = conversations.find(conv => conv.user.id === targetUserId);
      if (existingConv) {
        handleSelectConversation(existingConv);
        return;
      }

      // If no conversation exists, fetch user info and create a new conversation object
      try {
        const response = await messageAPI.getUserInfo(targetUserId);
        const userInfo = response.data.user;

        // Create a temporary conversation object
        const newConversation = {
          user: userInfo,
          lastMessage: null,
          unreadCount: 0
        };

        setSelectedConversation(newConversation);
        setMessages([]); // No messages yet
      } catch (error) {
        console.error('Failed to load user info:', error);
      }
    };

    initializeConversation();
  }, [location.state?.userId]);

  // Setup socket listeners
  useEffect(() => {
    if (!socket) return;

    socket.on('new_message', (message) => {
      // If message is in current conversation, add it to messages
      if (selectedConversation &&
          (message.senderId === selectedConversation.user.id ||
           message.receiverId === selectedConversation.user.id)) {
        setMessages(prev => [...prev, message]);
        shouldScrollRef.current = true; // Trigger scroll to bottom
      }

      // Refresh conversations list
      fetchConversations();
    });

    socket.on('messages_read', ({ readBy }) => {
      // Update read status for messages
      if (selectedConversation && readBy === selectedConversation.user.id) {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          isRead: msg.senderId === user.id ? true : msg.isRead
        })));
      }
    });

    socket.on('new_notification', (notification) => {
      // Add new notification to the list
      setNotifications(prev => [notification, ...prev]);
    });

    return () => {
      socket.off('new_message');
      socket.off('messages_read');
      socket.off('new_notification');
    };
  }, [socket, selectedConversation, user]);

  // Handle scroll behavior when messages change
  useEffect(() => {
    if (messages.length === 0) return;

    // Scroll if flag is set (from sending message) or if at bottom
    if (shouldScrollRef.current) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
        }
      }, 0);
      shouldScrollRef.current = false;
    } else {
      // Check if user is at bottom
      const container = messagesContainerRef.current;
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;

        if (isAtBottom) {
          setTimeout(() => {
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }
          }, 0);
        }
      }
    }
  }, [messages]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await messageAPI.getConversations();
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await messageAPI.getMessages(otherUserId);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const token = localStorage.getItem('token');

      // Mark as read
      await axios.put(`/api/notifications/${notification.id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setNotifications(prev => prev.map(n =>
        n.id === notification.id ? { ...n, isRead: true, readAt: new Date() } : n
      ));

      // Navigate to invoice if related
      if (notification.relatedType === 'Invoice' && notification.relatedId) {
        navigate('/invoices');
      }
    } catch (error) {
      console.error('Failed to handle notification:', error);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    shouldScrollRef.current = true; // Scroll to bottom when opening conversation
    await fetchMessages(conversation.user.id);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!newMessage.trim() || !selectedConversation) return;

    try {
      // Set flag to scroll after message is sent
      shouldScrollRef.current = true;

      await messageAPI.sendMessage({
        receiverId: selectedConversation.user.id,
        message: newMessage
      });

      setNewMessage('');
      // The message will be added via socket event
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays === 1) {
      return t('messages.yesterday');
    } else if (diffInDays < 7) {
      return date.toLocaleDateString('pl-PL', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pastel-purple-100 via-pastel-blue-100 to-pastel-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple-500"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-purple-100 via-pastel-blue-100 to-pastel-pink-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
          <div className="flex h-full">
            {/* Sidebar */}
            <div className="w-full md:w-80 lg:w-96 border-r border-pastel-purple-200 dark:border-gray-700 flex flex-col">
              {/* Notifications Section */}
              {showNotifications && notifications.length > 0 && (
                <div className="border-b border-pastel-purple-200 dark:border-gray-700">
                  <div className="bg-gradient-to-r from-pastel-pink-500 to-pastel-purple-500 px-6 py-3 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Notifications</h3>
                    <button
                      onClick={() => setShowNotifications(false)}
                      className="text-white hover:text-gray-200 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto custom-scrollbar">
                    {notifications.slice(0, 5).map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`p-3 cursor-pointer border-b border-pastel-purple-100 dark:border-gray-700 transition-all duration-200 ${
                          notification.isRead
                            ? 'bg-white dark:bg-gray-800 hover:bg-pastel-purple-50 dark:hover:bg-gray-750'
                            : 'bg-pastel-purple-50 dark:bg-gray-750 hover:bg-pastel-purple-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-start space-x-2">
                          <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                            notification.isRead ? 'bg-gray-300' : 'bg-pastel-pink-500 animate-pulse'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {formatTime(notification.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">{t('messages.conversations')}</h2>
                {!showNotifications && notifications.filter(n => !n.isRead).length > 0 && (
                  <button
                    onClick={() => setShowNotifications(true)}
                    className="text-white hover:text-gray-200 transition-colors relative"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-pastel-pink-500 rounded-full">
                      {notifications.filter(n => !n.isRead).length}
                    </span>
                  </button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="flex items-center justify-center h-full px-4">
                    <p className="text-center text-gray-500 dark:text-gray-400">{t('messages.noConversations')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-pastel-purple-100 dark:divide-gray-700">
                    {conversations.map((conv) => (
                      <div
                        key={conv.user.id}
                        className={`p-4 cursor-pointer transition-all duration-200 ${
                          selectedConversation?.user.id === conv.user.id
                            ? 'bg-pastel-purple-100 dark:bg-gray-700 border-l-4 border-pastel-purple-500'
                            : 'hover:bg-pastel-purple-50 dark:hover:bg-gray-800'
                        }`}
                        onClick={() => handleSelectConversation(conv)}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pastel-purple-400 to-pastel-pink-400 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                            {conv.user.displayName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                                {conv.user.displayName}
                              </h3>
                              {conv.unreadCount > 0 && (
                                <span className="ml-2 bg-pastel-purple-500 text-white text-xs font-bold rounded-full px-2 py-1 min-w-[24px] text-center">
                                  {conv.unreadCount}
                                </span>
                              )}
                            </div>
                            {conv.lastMessage ? (
                              <>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                  {conv.lastMessage.senderId === user.id && (
                                    <span className="font-medium">{t('messages.you')}: </span>
                                  )}
                                  {conv.lastMessage.message.substring(0, 50)}
                                  {conv.lastMessage.message.length > 50 && '...'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                  {formatTime(conv.lastMessage.createdAt)}
                                </p>
                              </>
                            ) : (
                              <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                {t('messages.noMessages')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Main chat area */}
            <div className="flex-1 flex flex-col min-w-0">
              {!selectedConversation ? (
                <div className="flex items-center justify-center h-full bg-gradient-to-br from-pastel-purple-50 to-pastel-blue-50 dark:from-gray-900 dark:to-gray-800">
                  <div className="text-center px-4">
                    <svg className="w-24 h-24 mx-auto text-pastel-purple-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {t('messages.selectConversation')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      {t('messages.selectConversationHint')}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Chat header */}
                  <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-b border-pastel-purple-200 dark:border-gray-700 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pastel-purple-400 to-pastel-pink-400 flex items-center justify-center text-white font-semibold">
                        {selectedConversation.user.displayName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {selectedConversation.user.displayName}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedConversation.user.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Messages area */}
                  <div
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto bg-gradient-to-br from-pastel-purple-50 to-pastel-blue-50 dark:from-gray-900 dark:to-gray-800 p-6 space-y-4 chat-scrollbar"
                  >
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.senderId === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[70%] ${msg.senderId === user.id ? 'order-1' : 'order-2'}`}>
                          <div
                            className={`rounded-2xl px-4 py-2 shadow-md ${
                              msg.senderId === user.id
                                ? 'bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 text-white'
                                : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                            }`}
                          >
                            <p className="text-sm leading-relaxed break-words">{msg.message}</p>
                            <div className={`flex items-center justify-end space-x-2 mt-1 text-xs ${
                              msg.senderId === user.id ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              <span>{formatTime(msg.createdAt)}</span>
                              {msg.senderId === user.id && (
                                <span className={msg.isRead ? 'text-blue-200' : 'text-white/60'}>
                                  {msg.isRead ? '✓✓' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input area */}
                  <form
                    onSubmit={handleSendMessage}
                    className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border-t border-pastel-purple-200 dark:border-gray-700 px-6 py-4"
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={t('messages.typeMessage')}
                        className="flex-1 px-4 py-3 bg-pastel-purple-50 dark:bg-gray-700 border border-pastel-purple-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-pastel-purple-500 dark:focus:ring-pastel-purple-400 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim()}
                        className="px-6 py-3 rounded-xl bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 text-white font-medium hover:from-pastel-pink-500 hover:to-pastel-purple-500 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-pastel-purple-500 disabled:hover:to-pastel-blue-500"
                      >
                        {t('messages.send')}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;
